// T018: Effect Registry

import type { Effect, EffectType, EffectTrigger } from '../../types';
import {
  PierceEffect,
  RangedEffect,
  BlockEffect,
  CoverEffect,
  PoisonEffect,
  RewardEffect,
  SoulEffect,
  OverloadEffect,
  SlideEffect,
  AmbushEffect,
  ElectrifyEffect,
  DismantleEffect,
  MechaMergeEffect,
  DevourMergeEffect,
  ParasiteMergeEffect,
  ChargeEffect,
  DrainEffect,
  MagicResistEffect,
  DestroyResistEffect,
  BindEffect,
  BoardEffect,
  ReviveEffect,
  TransformEffect,
  // New effects
  DecayEffect,
  VanishEffect,
  ArmorBreakEffect,
  TauntEffect,
  StealthEffect,
  FlyingEffect,
} from './Effect';

/**
 * Registry for all game effects.
 * Uses singleton pattern for global access.
 */
class EffectRegistryClass {
  private effects: Map<EffectType, Effect> = new Map();

  constructor() {
    this.registerDefaultEffects();
  }

  /**
   * Register default effects.
   */
  private registerDefaultEffects(): void {
    // Attack effects
    this.register(new PierceEffect());
    this.register(new RangedEffect());
    this.register(new SlideEffect());
    this.register(new AmbushEffect());

    // Defense effects
    this.register(new BlockEffect());
    this.register(new CoverEffect());
    this.register(new ElectrifyEffect());

    // Combat effects
    this.register(new PoisonEffect());
    this.register(new DismantleEffect());
    this.register(new BindEffect());

    // Merge effects
    this.register(new MechaMergeEffect());
    this.register(new DevourMergeEffect());
    this.register(new ParasiteMergeEffect());

    // Resource effects
    this.register(new RewardEffect());
    this.register(new ChargeEffect());
    this.register(new DrainEffect());
    this.register(new OverloadEffect());

    // Resistance effects
    this.register(new MagicResistEffect());
    this.register(new DestroyResistEffect());

    // Transport effects
    this.register(new BoardEffect());

    // Soul system
    this.register(new SoulEffect());
    this.register(new ReviveEffect());

    // Other
    this.register(new TransformEffect());
    
    // New effects (Phase 2)
    this.register(new DecayEffect());
    this.register(new VanishEffect());
    this.register(new ArmorBreakEffect());
    this.register(new TauntEffect());
    this.register(new StealthEffect());
    this.register(new FlyingEffect());
  }

  /**
   * Register an effect.
   */
  register(effect: Effect): void {
    this.effects.set(effect.type, effect);
  }

  /**
   * Get an effect by type.
   */
  get(type: EffectType): Effect | undefined {
    return this.effects.get(type);
  }

  /**
   * Get all effects for a specific trigger.
   */
  getByTrigger(trigger: EffectTrigger): Effect[] {
    const result: Effect[] = [];
    for (const effect of this.effects.values()) {
      if (effect.trigger === trigger) {
        result.push(effect);
      }
    }
    // Sort by priority (lower = higher priority)
    return result.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Get all effects for multiple types.
   */
  getMultiple(types: EffectType[]): Effect[] {
    const result: Effect[] = [];
    for (const type of types) {
      const effect = this.effects.get(type);
      if (effect) {
        result.push(effect);
      }
    }
    // Sort by priority
    return result.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Check if an effect type is registered.
   */
  has(type: EffectType): boolean {
    return this.effects.has(type);
  }

  /**
   * Get all registered effect types.
   */
  getAllTypes(): EffectType[] {
    return Array.from(this.effects.keys());
  }

  /**
   * Clear all registered effects (useful for testing).
   */
  clear(): void {
    this.effects.clear();
  }

  /**
   * Reset to default effects (useful for testing).
   */
  reset(): void {
    this.clear();
    this.registerDefaultEffects();
  }
}

// Singleton instance
export const EffectRegistry = new EffectRegistryClass();
